import React, { useState, useMemo, useEffect } from 'react';
import './SetCard.css';
import './SearchCard.css';
import { Category } from '@mui/icons-material';
import SetView from './SetView';
import StatsList from '../StatsList';
import { useOverlay } from '../../contexts/OverlayContext';
import SetHeader from './SetHeader';
import ItemHeader from './ItemHeader';
import ItemView from './ItemView';
import OverlayEquipFooter from '../OverlayEquipFooter';
import { useEquipItem } from '../../hooks/useEquipItem';
import localizedText from '../../utils/localizedText';
import { processSetBonuses } from '../../core/statProcessing';
import {
    buildElementGradient,
    buildElementGradientHover,
    buildElementIconFilter,
    buildElementIconFilterCompact
} from '../utils/CardGradient';
import { generatePulseDelay } from '../../utils/animationUtils';
import CardTopbar from './CardTopbar';
import MediaImg from '../utils/MediaImg';
import CompactCardTile from './CompactCardTile';

const TXT_SETNAME_ITEM_SET_PIECES_MAXPIECE = { fr: '${setName || \'Panoplie\'}, ${pieces}/${maxPieces} pièces. Appuyer pour les détails.', en: '${setName || \'Item set\'}, ${pieces}/${maxPieces} pieces. Tap for details.', de: '${setName || \'Set\'}, ${pieces}/${maxPieces} Teile. Für Details antippen.', es: '${setName || \'Set\'}, ${pieces}/${maxPieces} piezas. Pulsa para ver los detalles.', pt: '${setName || \'Conjunto\'}, ${pieces}/${maxPieces} peças. Toque para ver os detalhes.' };
const TXT_SET = { fr: 'Panoplie', en: 'Set', de: 'Set', es: 'Set', pt: 'Conjunto' };

const SHOW_COMPACT_STAT_LABELS = true;
const COMPACT_MULTI_COLUMN_STATS = true;
const SHOW_MULTI_COLUMN_LABELS = true;
const COMPACT_LABEL_MODE = 'alias';
/* Cells in a compact tile's thumbnail grid. A set with more pieces than this
   spends the last cell on a +N badge instead of a piece, so the grid stays the
   same square block whatever the set's size — pieces five and up wrapped onto a
   third row and pushed the tile out of the grid's rhythm. */
const COMPACT_TILE_THUMBNAILS = 4;
const TXT_EQUIP = { fr: 'Équiper', en: 'Equip', de: 'Ausrüsten', es: 'Equipar', pt: 'Equipar' };

const SetCard = React.forwardRef(({
    set,
    lang = 'fr',
    onSetClick,
    onItemClick,
    onTooltipShow,
    onTooltipHide,
    onSetTooltipShow,
    onSetTooltipHide,
    compact = false,
    compactVariant = 'strip',
    dense = false,
    isGenerating = false,
    isServerUpdate = false,
    preferredSlotId = null,
    className = '',
}, ref) => {
    const { showOverlay, hideOverlay } = useOverlay();
    const { equipItem, equipSet } = useEquipItem(lang);
    const [isHovered, setIsHovered] = useState(false);

    // Default to highest tier (last index) for non-compact, or active tier for compact
    const allBonuses = useMemo(() => set?.bonuses || [], [set?.bonuses]);
    const pieces = set?.pieces || 0;
    const isCompactPanel = compact && compactVariant === 'panel';
    const isCompactStrip = compact && compactVariant === 'strip';

    // For compact cards, show active bonus tier based on pieces
    // For non-compact cards, show highest tier by default
    const getInitialTierIndex = () => {
        if (compact && pieces >= 2 && allBonuses.length > 0) {
            // Find the bonus tier that matches the current pieces count
            // Bonuses are indexed by pieces - 2 (2 pieces = index 0, 3 pieces = index 1, etc.)
            const activeTierIndex = pieces - 2;
            return Math.min(activeTierIndex, allBonuses.length - 1);
        }
        return allBonuses.length > 0 ? allBonuses.length - 1 : 0;
    };

    const [selectedTierIndex, setSelectedTierIndex] = useState(getInitialTierIndex());

    // Update selected tier when pieces change (for compact cards)
    useEffect(() => {
        if (compact && pieces >= 2 && allBonuses.length > 0) {
            const activeTierIndex = pieces - 2;
            const newIndex = Math.min(activeTierIndex, allBonuses.length - 1);
            setSelectedTierIndex(newIndex);
        }
    }, [compact, pieces, allBonuses.length]);

    // Generate random pulse delay - must be called before early return
    const pulseDelay = useMemo(() => generatePulseDelay(), []);

    const setMaxTierStatsMap = useMemo(() => {
        if (!allBonuses.length) return null;
        const maxTier = allBonuses[allBonuses.length - 1];
        if (!maxTier || !Array.isArray(maxTier.effects)) return null;
        const statsMap = {};
        maxTier.effects.forEach((effect) => {
            const statId = effect.characteristic;
            if (!statId) return;
            const value = effect.value !== undefined ? effect.value : (effect.min !== undefined ? effect.min : (effect.max !== undefined ? effect.max : 0));
            if (!statsMap[statId]) statsMap[statId] = 0;
            statsMap[statId] += value;
        });
        return statsMap;
    }, [allBonuses]);

    if (!set) return null;

    const setName = localizedText(set.name, lang);
    const maxPieces = set.max_pieces || (set.items ? set.items.length : 0);

    // Calculate highest level from set items
    const setItems = set.items || [];
    const maxLevel = setItems.length > 0
        ? Math.max(...setItems.map(item => item.level || 0).filter(level => level > 0))
        : 0;

    // Gradient from max bonus tier (non-compact only)
    let gradientBackground = undefined;
    let gradientBackgroundHover = undefined;
    if (!compact && setMaxTierStatsMap) {
        gradientBackground = buildElementGradient(setMaxTierStatsMap, compact);
        gradientBackgroundHover = buildElementGradientHover(setMaxTierStatsMap, compact);
    }

    // Extract effects from the selected tier bonus (strip mode skips stats UI)
    // Backend format: [{ num_items_required: 2, effects: [{ characteristic, min, max }] }, ...]
    let processedBonuses = [];
    if (!isCompactStrip && !dense && allBonuses.length > 0 && selectedTierIndex >= 0 && selectedTierIndex < allBonuses.length) {
        const selectedTier = allBonuses[selectedTierIndex];
        // Ceilings this tier puts on a characteristic (only Cire Momore has any).
        // They ride inside the tier in the raw/catalog shape and in a parallel list
        // in the set-detail one — see backend core/set_caps.py.
        const tierCaps = selectedTier?.caps || set?.bonus_caps?.[selectedTierIndex] || [];
        if (selectedTier) {
            // Handle new format with effects array
            if (selectedTier.effects && Array.isArray(selectedTier.effects)) {
                // Transform effects to format expected by processSetBonuses
                // Server may send { characteristic, value } or { characteristic, min, max }
                const transformedEffects = selectedTier.effects.map(effect => ({
                    characteristic: effect.characteristic,
                    value: effect.value !== undefined ? effect.value : (effect.min !== undefined ? effect.min : (effect.max !== undefined ? effect.max : 0))
                }));
                // processSetBonuses expects an array containing an array of effects
                processedBonuses = processSetBonuses([transformedEffects], lang, tierCaps);
            }
            // Handle old format (array of effects directly)
            else if (Array.isArray(selectedTier)) {
                // Old format: tier is directly an array of effects
                const transformedEffects = selectedTier.map(effect => ({
                    characteristic: effect.characteristic,
                    value: effect.value || (effect.min !== undefined ? effect.min : (effect.max !== undefined ? effect.max : 0))
                }));
                processedBonuses = processSetBonuses([transformedEffects], lang, tierCaps);
            }
        }
    }

    const handleTierButtonClick = (e, tierIndex) => {
        e.stopPropagation(); // Prevent card click
        setSelectedTierIndex(tierIndex);
    };

    const openSetOverlay = () => {
        const equip = onSetClick
            ? () => onSetClick(set)
            : () => equipSet(set, preferredSlotId);
        showOverlay(
            [
                <SetHeader key={`set-hdr-${set.id}`} set={set} lang={lang} />,
                <SetView
                    key={`set-view-${set.id}`}
                    set={set}
                    lang={lang}
                    onItemClick={onItemClick || ((item) => equipItem(item, preferredSlotId))}
                />,
            ],
            { footer: <OverlayEquipFooter lang={lang} onEquip={equip} /> }
        );
    };

    const handleCardClick = () => {
        if (preferredSlotId != null) {
            if (onSetClick) {
                onSetClick(set);
                hideOverlay();
            }
            return;
        }
        openSetOverlay();
    };

    const handleEquipClick = (e) => {
        e.stopPropagation();
        if (onSetClick) {
            onSetClick(set);
            return;
        }
        equipSet(set, preferredSlotId);
    };

    const handleItemThumbnailMouseEnter = (item, event) => {
        if (onTooltipShow) {
            onTooltipShow(item, event);
        }
    };

    const handleItemThumbnailMouseLeave = () => {
        if (onTooltipHide) {
            onTooltipHide();
        }
    };

    const handleSetMouseEnter = (event) => {
        if (onSetTooltipShow && set) {
            onSetTooltipShow(set, event);
        }
    };

    const handleSetMouseLeave = () => {
        if (onSetTooltipHide) {
            onSetTooltipHide();
        }
    };

    const openPieceOverlay = (item) => {
        if (!item) return;
        const equip = onItemClick
            ? () => onItemClick(item)
            : () => equipItem(item, preferredSlotId);
        showOverlay(
            [
                <ItemHeader key={`item-hdr-${item.id}`} item={item} lang={lang} />,
                <ItemView key={`item-view-${item.id}`} item={item} lang={lang} compact={false} />,
            ],
            { footer: <OverlayEquipFooter lang={lang} onEquip={equip} /> }
        );
    };

    const handleItemThumbnailClick = (event, item) => {
        event.stopPropagation();
        openPieceOverlay(item);
    };

    /* A piece's own elements tint its glow; a piece with no stats of its own
       borrows the set's, so it does not sit grey among lit neighbours. */
    const thumbnailGlow = (item, dimmed = false) => {
        const em = item?.effects_map;
        const hasItemStats = em && typeof em === 'object' && Object.keys(em).length > 0;
        const stats = hasItemStats ? em : (setMaxTierStatsMap || {});
        return dimmed ? buildElementIconFilterCompact(stats) : buildElementIconFilter(stats);
    };

    const renderItemThumbnail = (item, index) => {
        if (!item) return null;
        const itemName = localizedText(item.name, lang);
        const isActive = item.isActive !== false; // Default to true if not specified (for backward compatibility)

        // For compact cards, make inactive items transparent
        const opacity = compact && !isActive ? 0.3 : 1;
        const iconFilter = thumbnailGlow(item);

        const img = (
            <MediaImg
                src={item.icon || '/img/stat_re_armes.png'}
                alt={isCompactStrip ? '' : itemName}
                className="set-card-item-thumbnail-img"
                style={{
                    filter: iconFilter,
                    ...(compact && !isActive ? { opacity } : {}),
                }}
                retry={Boolean(item.icon)}
                fill
            />
        );

        /* Build strip: decorative only — clicks open set overlay on the parent card */
        if (isCompactStrip) {
            return (
                <span key={index} className="set-card-item-thumbnail" aria-hidden="true">
                    {img}
                </span>
            );
        }

        return (
            <button
                key={index}
                type="button"
                className="set-card-item-thumbnail"
                onClick={(e) => handleItemThumbnailClick(e, item)}
                onMouseEnter={(e) => handleItemThumbnailMouseEnter(item, e)}
                onMouseLeave={handleItemThumbnailMouseLeave}
            >
                {img}
            </button>
        );
    };

    const statsListNode = processedBonuses.length > 0 ? (
        <div className="item-card-stats-wrapper">
            <StatsList
                stats={processedBonuses}
                className="item-card-stats-col"
                compact={isCompactPanel}
                compactMultiColumn={isCompactPanel ? COMPACT_MULTI_COLUMN_STATS : true}
                showCompactLabels={isCompactPanel ? SHOW_COMPACT_STAT_LABELS : false}
                showLabelsInMultiColumn={isCompactPanel ? SHOW_MULTI_COLUMN_LABELS : false}
                labelMode={isCompactPanel ? COMPACT_LABEL_MODE : 'label'}
                lang={lang}
            />
        </div>
    ) : null;

    /* Build generator compact: only icons for pieces actually equipped (isActive), not name/stats */
    let stripItems = [];
    if (isCompactStrip) {
        stripItems = setItems.filter((i) => i && i.isActive === true);
        if (stripItems.length === 0 && pieces > 0 && setItems.length > 0) {
            stripItems = setItems.slice(0, Math.min(pieces, setItems.length));
        }
    }

    const stripAriaLabel = isCompactStrip
        ? (TXT_SETNAME_ITEM_SET_PIECES_MAXPIECE[lang] || TXT_SETNAME_ITEM_SET_PIECES_MAXPIECE.en)
        : undefined;

    // Compact list: the piece thumbnails stand in for the art an item card gets
    // from its icon, and everything else moves to the hover panel and to the set
    // overlay a tap opens — the same inspect-then-act the compact item tile has.
    const overflowsThumbnails = setItems.length > COMPACT_TILE_THUMBNAILS;
    const thumbnailItems = setItems.slice(0, overflowsThumbnails ? COMPACT_TILE_THUMBNAILS - 1 : COMPACT_TILE_THUMBNAILS);
    const hiddenThumbnails = setItems.length - thumbnailItems.length;
    const thumbnailCells = thumbnailItems.length + (hiddenThumbnails > 0 ? 1 : 0);

    if (dense) {
        return (
            <CompactCardTile
                ref={ref}
                info={(
                    <div className="set-card-info">
                        <h6 className="set-card-type">
                            <Category />
                        </h6>
                        {maxLevel > 0 && (
                            <h6 className="set-card-level">
                                <span className="set-card-level-value">{maxLevel}</span>
                            </h6>
                        )}
                    </div>
                )}
                equipLabel={TXT_EQUIP[lang] || TXT_EQUIP.en}
                onEquip={handleEquipClick}
                equipClassName="set-card-equip-button"
                art={(
                    <div className={`compact-card-set-thumbnails${thumbnailCells === COMPACT_TILE_THUMBNAILS ? ' compact-card-set-thumbnails--quad' : ''}`}>
                        {thumbnailItems.map((item, index) => (
                            <MediaImg
                                key={item?.id ?? index}
                                className="compact-card-set-thumbnail"
                                src={item?.icon || '/img/stat_re_armes.png'}
                                alt=""
                                style={{ filter: thumbnailGlow(item, true) }}
                                retry={Boolean(item?.icon)}
                            />
                        ))}
                        {hiddenThumbnails > 0 && (
                            <span className="compact-card-set-more">+{hiddenThumbnails}</span>
                        )}
                    </div>
                )}
                name={setName}
                gradient={gradientBackground}
                gradientHover={gradientBackgroundHover}
                onClick={openSetOverlay}
                // The tile carries thumbnails and a level, so the hover panel is
                // the card — and it is hoverable, the way the compact item tile's
                // is: the pointer can walk into it, click it for the set, or
                // click a piece for that piece's own overlay.
                onPointerEnter={onSetTooltipShow ? (e) => onSetTooltipShow(set, e, {
                    interactive: true,
                    onOpenSet: openSetOverlay,
                    onOpenItem: openPieceOverlay,
                }) : undefined}
                onPointerLeave={onSetTooltipHide ? (e) => onSetTooltipHide(e) : undefined}
            />
        );
    }

    return (
        <div
            className={`set-card card panel column ${compact ? 'set-card--compact' : ''} ${isCompactPanel ? 'set-card--compact-panel' : ''} ${isCompactStrip ? 'set-card--compact-strip' : ''} ${isServerUpdate ? 'pulse-effect' : ''} ${className}`.trim()}
            ref={ref}
            role={isCompactStrip ? 'button' : undefined}
            tabIndex={isCompactStrip ? 0 : undefined}
            aria-label={stripAriaLabel}
            style={isServerUpdate ? { '--pulse-delay': pulseDelay } : undefined}
            onClick={handleCardClick}
            onKeyDown={isCompactStrip ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick();
                }
            } : undefined}
            onMouseEnter={(e) => {
                setIsHovered(true);
                handleSetMouseEnter(e);
            }}
            onMouseLeave={(e) => {
                setIsHovered(false);
                handleSetMouseLeave();
            }}
        >
            <div
                className="set-card-gradient"
                style={gradientBackground ? {
                    background: isHovered && gradientBackgroundHover ? gradientBackgroundHover : gradientBackground
                } : undefined}
            >
                {!compact && (
                    <CardTopbar
                        info={(
                            <div className="set-card-info">
                                <h6 className="set-card-type">
                                    <Category sx={{ width: 16, height: 16 }} />
                                    <span>{TXT_SET[lang] || TXT_SET.en}</span>
                                </h6>
                                {maxLevel > 0 && <span className="card-info-dot" aria-hidden="true">•</span>}
                                {maxLevel > 0 && (
                                    <h6 className="set-card-level">
                                        <span className="set-card-level-value">{maxLevel}</span>
                                    </h6>
                                )}
                            </div>
                        )}
                        equipLabel={TXT_EQUIP[lang] || TXT_EQUIP.en}
                        onEquip={handleEquipClick}
                        equipClassName="set-card-equip-button"
                    />
                )}
                <div className="set-card-body">
                    <div className={isCompactStrip ? 'set-card-header set-card-header--strip-only' : 'set-card-header column'}>
                        {isCompactStrip ? (
                            <>
                                <div className="set-card-strip-thumbnails">
                                    {stripItems.map((item, index) => renderItemThumbnail(item, index))}
                                </div>
                                {maxPieces > 0 && (
                                    <span className="set-card-strip-count">{pieces}/{maxPieces}</span>
                                )}
                            </>
                        ) : isCompactPanel ? (
                            <>
                                <div className="set-card-compact-main">
                                    <div className="set-card-header-hero set-card-header-hero--compact">
                                        <div className="set-card-name-row row">
                                            <h5 className="set-card-name">{setName}</h5>
                                            <div className="set-tooltip-pieces">
                                                {pieces}
                                                <span className="set-tooltip-pieces-span">/{maxPieces}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {statsListNode}
                                </div>
                                {setItems.length > 0 && (
                                    <div className="set-card-items-thumbnails row">
                                        {setItems.map((item, index) => renderItemThumbnail(item, index))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {setItems.length > 0 && (
                                    <div className="set-card-art set-card-items-thumbnails row">
                                        {setItems.map((item, index) => renderItemThumbnail(item, index))}
                                    </div>
                                )}
                                <div className="set-card-header-hero">
                                    <div className="set-card-name-row row">
                                        <h5 className="set-card-name">{setName}</h5>
                                    </div>
                                    {allBonuses.length > 0 && (
                                        <div className="set-card-tier-buttons row">
                                            {allBonuses.map((tier, index) => {
                                                const numItems = tier.num_items_required || (index + 1) * 2;
                                                const isSelected = index === selectedTierIndex;
                                                return (
                                                    <button
                                                        key={index}
                                                        className={`set-card-tier-button ${isSelected ? 'active' : ''}`}
                                                        onClick={(e) => handleTierButtonClick(e, index)}
                                                    >
                                                        {numItems}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {statsListNode}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default React.memo(SetCard);
